import { useMemo } from 'react';
import { DashboardIconStatic } from './DashboardIconStatic';

interface DashboardIconAnimatedProps {
  width?: number;
  height?: number;
  className?: string;
  duration?: number;
  easing?: string;
  color?: string;
  hoverColor?: string;
  strokeWidth?: number;
}

export const DashboardIconAnimated = ({
  width = 107,
  height = 107,
  className,
  duration = 0.5,
  easing = 'ease-in-out',
  color = 'currentColor',
  hoverColor = color,
  strokeWidth = 8
}: DashboardIconAnimatedProps) => {
  // Pre-calculate path data for better performance
  const pathData = useMemo(() => {
    // Normal state values
    const normalY = 8.82813;
    const normalHLine1 = 38.6099;
    const normalHLine2 = 68.3916;

    // Hovered state values - with more movement
    const hoveredY = 9.63623;
    const hoveredHLine1 = 34.418;
    const hoveredHLine2 = 74.1997;

    // Build paths for both states
    const buildMainPath = (y: number) => {
      return `M53.5087 ${y}L78.76 ${y}C89.4852 ${y} 98.1797 ${y + 8.7025} 98.1797 ${y + 19.4277}L98.1797 ${y + 69.9342}C98.1797 ${y + 80.6593} 89.4852 ${y + 89.3538} 78.76 ${y + 89.3538}L53.5184 ${y + 89.3538}M53.5087 ${y}L28.2535 ${y}C17.5284 ${y} 8.83391 ${y + 8.7025} 8.83391 ${y + 19.4277}L8.83391 ${y + 69.9342}C8.83391 ${y + 80.6593} 17.5284 ${y + 89.3538} 28.2535 ${y + 89.3538}H53.5184M53.5087 ${y}L53.5184 ${y + 89.3538}`;
    };

    const buildHLine = (y: number) => `M8.83501 ${y}L53.5195 ${y}`;

    return {
      mainPath: buildMainPath(normalY),
      hoveredMainPath: buildMainPath(hoveredY),
      line1: buildHLine(normalHLine1),
      hoveredLine1: buildHLine(hoveredHLine1),
      line2: buildHLine(normalHLine2),
      hoveredLine2: buildHLine(hoveredHLine2)
    };
  }, []);

  // Convert CSS ease-in-out to SVG ease equivalent
  const svgEasing = easing === 'ease-in-out' ? 'ease' : easing;

  // SVG animation properties for continuous animation
  const animationAttributes = {
    dur: `${duration * 2}s`,
    begin: 'dashboardParentGroup.mouseover',
    end: 'dashboardParentGroup.mouseout',
    repeatCount: 'indefinite',
    calcMode: 'spline',
    keySplines: '0.42 0 0.58 1; 0.42 0 0.58 1',
    keyTimes: '0; 0.5; 1',
    restart: 'whenNotActive'
  };

  return (
    <div className='group'>
      {/* Static version - visible by default, hidden on hover */}
      <div className={`group-hover:hidden ${className}`} style={{ width, height }}>
        <DashboardIconStatic
          width={width}
          height={height}
          color={color}
          strokeWidth={strokeWidth}
        />
      </div>

      {/* Animated version - hidden by default, visible on hover */}
      <div className={`hidden group-hover:block ${className}`} style={{ width, height }}>
        <svg
          width={width}
          height={height}
          viewBox="0 0 107 107"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="dashboardParentGroup">
            <rect width="106.808" height="106.808" fill="none">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0; 0 1.2; 0 0"
                {...animationAttributes}
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                additive="sum"
                values="0 53.5 53.5; 0.4 53.5 53.5; 0 53.5 53.5"
                {...animationAttributes}
              />
            </rect>

            <path
              d={pathData.mainPath}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <animate
                attributeName="d"
                values={`${pathData.mainPath}; ${pathData.hoveredMainPath}; ${pathData.mainPath}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <path
              d={pathData.line1}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <animate
                attributeName="d"
                values={`${pathData.line1}; ${pathData.hoveredLine1}; ${pathData.line1}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <path
              d={pathData.line2}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <animate
                attributeName="d"
                values={`${pathData.line2}; ${pathData.hoveredLine2}; ${pathData.line2}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <rect
              width="107"
              height="107"
              fill="transparent"
              style={{ cursor: 'pointer' }}
            />
          </g>
        </svg>
      </div>
    </div>
  );
};
